'use server';
import { actionErrorMessage } from '@/shared/infrastructure/logging/actionError';

import { SessionService } from '@/contexts/authentication';
import { guardIllustrationEdit } from '@/contexts/pipeline-v2/edits/guardAction.server';

/**
 * Change how ONE cast member looks, without touching the story.
 *
 * Until now a parent who disagreed with a character's look had two options:
 * press "redraw" and get another render of the SAME words, or recreate the whole
 * book. The words were the thing that needed changing, and nothing could change
 * them — so "the hunters look nothing like hunters" had no fix short of starting
 * over.
 *
 * This writes the new description into the book's cast sheet (what the
 * illustrator reads for every page) AND into the portrait decision (what a
 * reference render is built from). The pages that are already drawn keep their
 * pictures; the ones drawn after this follow the new words, and the reference
 * can be redrawn right away from the panel.
 */
export async function updateCastDescription(input: {
  bookId: string;
  extractedName: string;
  visualDescription: string;
  baseOutfit?: string;
}): Promise<{ success: boolean; error?: string }> {
  try {
    const familyId = await SessionService.getFamilyId();
    if (!familyId) return { success: false, error: 'Not authenticated' };

    // «Описание / наряд»: waits for the generation; frozen while a print order is open
    const blocked = await guardIllustrationEdit({ illustrationId: input.bookId, familyId, kind: 'entity_look' });
    if (blocked) return { success: false, error: blocked.message };

    const description = input.visualDescription.trim();
    if (!description) return { success: false, error: 'EMPTY_DESCRIPTION' };

    const { getIllustrationRepository } = await import('@/contexts/illustrations/repositories');
    const repo = getIllustrationRepository();
    const illustration = await repo.getById(input.bookId, familyId);
    if (!illustration) return { success: false, error: 'BOOK_NOT_FOUND' };

    const nameKey = input.extractedName.trim().toLowerCase();
    const analysis = illustration.storyAnalysis;
    const { splitBlobIntoMembers } = await import('@/contexts/illustrations/services/castRepair');
    const characters = (analysis?.characters ?? []).map((c) => {
      if (c.name.trim().toLowerCase() !== nameKey) return c;
      // A troupe written as one paragraph with a block per member is stored as
      // members, so the group sheet draws three SPECIFIC people instead of
      // averaging one wall of prose into three strangers.
      const split = (c as { group?: boolean }).group ? splitBlobIntoMembers(description) : { head: description, members: [] };
      return {
        ...c,
        visualDescription: split.members.length ? split.head : description,
        ...(split.members.length ? { members: split.members } : {}),
        ...(input.baseOutfit !== undefined ? { baseOutfit: input.baseOutfit.trim() } : {}),
      };
    });
    if (!characters.some((c) => c.name.trim().toLowerCase() === nameKey)) {
      return { success: false, error: 'CHARACTER_NOT_FOUND' };
    }

    // The portrait step reads the DECISION, not the analysis — both have to say
    // the same thing or a redraw would quietly use the old words.
    // What the sheet is actually drawn from: the summary plus every member, the
    // same string the illustrator gets on a page.
    const { describeWithMembers } = await import('@/contexts/illustrations/services/castRepair');
    const updated = characters.find((c) => c.name.trim().toLowerCase() === nameKey);
    const forPortrait = describeWithMembers(updated as never) || description;
    const decisions = (illustration.characterDecisions ?? []).map((d) =>
      d.extractedName.trim().toLowerCase() === nameKey
        ? { ...d, createData: { ...(d.createData ?? { name: d.extractedName }), description: forPortrait } }
        : d,
    );

    await repo.update(input.bookId, familyId, {
      storyAnalysis: { ...analysis, characters } as never,
      characterDecisions: decisions as never,
    });

    return { success: true };
  } catch (e: unknown) {
    return { success: false, error: actionErrorMessage('updateCastDescription', e) };
  }
}
